import type { StaticRemote } from '@cosmica-shell-ui/shell-shared'

import { remoteMap } from './remoteMap'
import { remotePlatformMap } from './remotePlatformMap'
import {logger} from "@mosaic/logger-service";

const REMOTE_OVERRIDE_PREFIX = 'remote.'
const REMOTE_ENTRY_FILE = 'remoteEntry.js'

function isRemoteOverrideAllowed(): boolean {
  return __REMOTE_OVERRIDE_ALLOWED__
}


/**
 * Returns the name used by the shell to resolve a remote.
 */
function getRemoteName(remote: Pick<StaticRemote, 'name'>): string {
  return remote.name
}

function getShellKnownAliases(remotes: StaticRemote[]): string[] {
  return [...remotes, ...remotePlatformMap].map(getRemoteName)
}

function getShellKnownPlatformAliases(remotes: StaticRemote[]): string[] {
  return [...remoteMap, ...remotes].map(getRemoteName)
}

export interface ParsedRemoteOverride {
  name: string
  parameter: string
  rawValue: string
}

/**
 * Normalizes a remote override URL to the final remote entry URL.
 *
 * Accepted inputs are HTTP/HTTPS base URLs or fully qualified remoteEntry URLs.
 * Invalid or unsupported values return {@link null} and are ignored by the caller.
 *
 * @param value - The raw override value read from the query string.
 * @returns The normalized remote entry URL, or {@link null} when the value is invalid.
 */
export function normalizeRemoteEntryUrl(value: string): string | null {
  const trimmedValue = value.trim()

  if (!trimmedValue) {
    return null
  }

  try {
    const url = new URL(trimmedValue)

    if (!['http:', 'https:'].includes(url.protocol)) {
      return null
    }

    if (url.pathname.endsWith(`/${REMOTE_ENTRY_FILE}`)) {
      return url.toString()
    }

    const normalizedPathname = url.pathname.replace(/\/+$/, '')
    url.pathname = `${normalizedPathname}/${REMOTE_ENTRY_FILE}`

    return url.toString()
  } catch {
    return null
  }
}

/**
 * @param search - The browser query string.
 * @returns The list of raw remote override parameters found in the query string.
 */
export function parseRemoteOverrideParams(search: string): ParsedRemoteOverride[] {
  const searchParams = new URLSearchParams(search)
  const overrides: ParsedRemoteOverride[] = []

  for (const [rawKey, rawValue] of searchParams.entries()) {
    if (!rawKey.startsWith(REMOTE_OVERRIDE_PREFIX)) {
      continue
    }

    overrides.push({
      name: rawKey.slice(REMOTE_OVERRIDE_PREFIX.length),
      parameter: rawKey,
      rawValue
    })
  }

  return overrides
}

/**
 * Logs warnings for override aliases that are not known by the shell as a whole.
 *
 * @param overrides - Parsed override parameters from the current query string.
 * @param knownAliases - The full set of aliases supported by the shell.
 */
export function warnUnknownRemoteOverrideAliases(
  overrides: ParsedRemoteOverride[],
  knownAliases: string[]
): void {
  const knownAliasesSet = new Set(knownAliases)

  for (const override of overrides) {
    if (!knownAliasesSet.has(override.name)) {
      logger.warn('Ignoring override for unknown remote name.', {
        name: override.name,
        parameter: override.parameter
      })
    }
  }
}

function collectKnownRemoteOverrides(
  remotes: StaticRemote[],
  overrides: ParsedRemoteOverride[]
): Map<string, string> {
  const knownAliasesSet = new Set(remotes.map(getRemoteName))
  const resolvedOverrides = new Map<string, string>()

  for (const override of overrides) {
    if (!knownAliasesSet.has(override.name)) {
      continue
    }

    const normalizedUrl = normalizeRemoteEntryUrl(override.rawValue)

    if (!normalizedUrl) {
      logger.warn('Ignoring override with invalid remote URL.', {
        name: override.name,
        value: override.rawValue
      })
      continue
    }

    resolvedOverrides.set(override.name, normalizedUrl)
    logger.info('Resolved development override for remote name.', {
      name: override.name,
      entry: normalizedUrl
    })
  }

  return resolvedOverrides
}

export interface RemoteOverrideEntry {
  name: string
  entry: string
}

export interface ResolvedRemotes {
  remotes: StaticRemote[]
  overrides: RemoteOverrideEntry[]
}

/**
 * Applies the relevant overrides to the provided remote list.
 */
function applyRemoteOverrides(remotes: StaticRemote[], overrides: ParsedRemoteOverride[]): ResolvedRemotes {
  if (!isRemoteOverrideAllowed()) {
    return { remotes: remotes.map((remote) => ({ ...remote })), overrides: [] }
  }

  const resolvedOverrides = collectKnownRemoteOverrides(remotes, overrides)

  const resolvedRemotes = remotes.map((remote) => {
    const name = getRemoteName(remote)
    const overrideEntry = resolvedOverrides.get(name)

    if (!overrideEntry) {
      return { ...remote }
    }

    return {
      ...remote,
      entry: overrideEntry
    }
  })

  const appliedOverrides: RemoteOverrideEntry[] = [...resolvedOverrides.entries()].map(([name, entry]) => ({
    name,
    entry: new URL(entry).host
  }))

  return { remotes: resolvedRemotes, overrides: appliedOverrides }
}

/**
 * Resolves overrides for application remotes.
 *
 * @param search - The browser query string.
 * @returns The application remote list with applicable overrides applied.
 */
export function resolveApplicationRemotes(search: string): ResolvedRemotes {
  const parsedOverrides = parseRemoteOverrideParams(search)

  if (isRemoteOverrideAllowed()) {
    warnUnknownRemoteOverrideAliases(parsedOverrides, getShellKnownAliases(remoteMap))
  }

  return applyRemoteOverrides(remoteMap, parsedOverrides)
}

/**
 * Resolves overrides for platform remotes.
 *
 * @param search - The browser query string.
 * @returns The platform remote list with applicable overrides applied.
 */
export function resolvePlatformRemotes(search: string): ResolvedRemotes {
  const parsedOverrides = parseRemoteOverrideParams(search)

  if (isRemoteOverrideAllowed()) {
    warnUnknownRemoteOverrideAliases(parsedOverrides, getShellKnownPlatformAliases(remotePlatformMap))
  }

  return applyRemoteOverrides(remotePlatformMap, parsedOverrides)
}
